import type { TmsInvoiceRow } from '@numeo/types-main-web-app'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ds2'
import { formatCount, formatUsd } from '../results/format'
import { emailBlockOf, invoiceBlockOf, statusOf } from './invoicingRows'
import type { BatchMode } from './CreateBatchDrawer'

/** The Invoices page, where closing and deleting invoices stay. */
const INVOICES_PAGE = '/accounting/invoices'

const INVOICED = new Set(['draft', 'sent', 'batch_prepared'])

interface SelectionBarProps {
    rows: TmsInvoiceRow[]
    /** The selected loads' amounts summed: the invoice total where the load has one, else its line haul. */
    total: number
    factoringConnected: boolean
    /** Opens the one load's review drawer, where its invoice is created. */
    onCreateInvoice: (row: TmsInvoiceRow) => void
    onBatch: (mode: BatchMode) => void
    onSend: () => void
}

/**
 * The bar under the table while loads are selected, with the Invoices page's Ready to Invoice actions: Create invoice (one load) or
 * Create batch (several), Add to batch, and the send the factoring connection allows. Closing and deleting stay on the page; the
 * selection is cleared with the table's checkboxes.
 */
export const SelectionBar = ({ rows, total, factoringConnected, onCreateInvoice, onBatch, onSend }: SelectionBarProps) => {
    // The page's rule: loads are batched only while none of them is invoiced and none is billed through Amazon.
    const uninvoiced = rows.every((row) => !INVOICED.has(statusOf(row)))
    const amazon = rows.some((row) => row.billed_through_amazon)
    const canBatch = uninvoiced && !amazon
    const sendable = rows.every((row) => (factoringConnected ? invoiceBlockOf(row) : emailBlockOf(row)) === null)
    const sendLabel = factoringConnected ? 'Submit to factoring' : 'Send by email'
    const hint = amazon
        ? 'Amazon-billed loads are invoiced through Amazon.'
        : !canBatch && !sendable
          ? 'Select loads that are not invoiced yet to batch them, or invoices to send.'
          : null

    return (
        <div className="flex flex-col gap-2 rounded-xl border border-accent-soft bg-accent-soft px-3 py-2.5 text-xs text-accent-soft-foreground">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <span>
                    <strong className="font-semibold">{formatCount(rows.length)} selected</strong> · {formatUsd(total)}
                </span>
                <div className="flex flex-wrap items-center gap-2">
                    {canBatch && (
                        <Button size="sm" variant="secondary" onClick={() => onBatch('existing')}>
                            Add to batch
                        </Button>
                    )}
                    {sendable && (
                        <Button size="sm" variant={canBatch ? 'secondary' : 'primary'} onClick={onSend}>
                            {sendLabel}
                        </Button>
                    )}
                    {canBatch &&
                        (rows.length === 1 ? (
                            <Button size="sm" onClick={() => onCreateInvoice(rows[0])}>
                                Create invoice
                            </Button>
                        ) : (
                            <Button size="sm" onClick={() => onBatch('new')}>
                                Create batch
                            </Button>
                        ))}
                </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 text-foreground-muted">
                <span>{hint ?? ''}</span>
                <Link to={INVOICES_PAGE} target="_blank" rel="noopener noreferrer" className="font-medium text-accent-soft-foreground hover:underline">
                    Continue on the Invoices page →
                </Link>
            </div>
        </div>
    )
}
